import React, { useEffect, useState } from 'react';
import { locationService } from '../../api/locationService';
import useActiveRegions from '../../hooks/useActiveRegions';
import {
  locationOptionId,
  locationOptionName,
  unwrapLocationList,
} from '../../constants/kenyaLocations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';

/** Matches outline Button styling; light focus ring so border does not look heavy when open. */
const BUTTON_TRIGGER_CLASS =
  'w-[200px] h-9 rounded-xl border border-primary bg-white text-[#000000] hover:bg-[#E0F8F3] font-medium text-sm focus:outline-none focus:!ring-1 focus:!ring-primary/30 focus:!border-primary';

const DEFAULT_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60';

/**
 * Optional Region → County cascade filter for reports.
 * regionValue / countyValue: '' | 'all' = All; otherwise id string/number.
 *
 * @param {'default'|'button'} [variant='default']
 *   - default: labeled native selects
 *   - button: outline button-style triggers (same family as Export/Refresh)
 */
export default function CountyFilterSelect({
  regionValue = 'all',
  countyValue = 'all',
  onRegionChange,
  onCountyChange,
  /** @deprecated use countyValue */
  value,
  /** @deprecated use onCountyChange */
  onChange,
  className = '',
  regionId = 'region-filter',
  countyId = 'county-filter',
  regionLabel = 'Region',
  countyLabel = 'County',
  disabled = false,
  variant = 'default',
}) {
  const resolvedCountyValue = countyValue ?? value ?? 'all';
  const handleCountyChange = onCountyChange ?? onChange;
  const isButton = variant === 'button';

  const { regions, loading: loadingRegions } = useActiveRegions();
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const regionIdNum = regionFilterToId(regionValue);

    (async () => {
      setLoadingCounties(true);
      try {
        const response =
          regionIdNum == null
            ? await locationService.getActiveCounties()
            : await locationService.getActiveCounties(null, regionIdNum);
        if (!cancelled) {
          setCounties(unwrapLocationList(response));
        }
      } catch (error) {
        console.error('Error loading counties for report filter:', error);
        if (!cancelled) setCounties([]);
      } finally {
        if (!cancelled) setLoadingCounties(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [regionValue]);

  const selectRegion = regionValue == null || regionValue === '' ? 'all' : String(regionValue);
  const selectCounty =
    resolvedCountyValue == null || resolvedCountyValue === '' ? 'all' : String(resolvedCountyValue);

  const onRegionSelect = (next) => {
    const value = next === 'all' ? 'all' : next;
    onRegionChange?.(value);
    handleCountyChange?.('all');
  };

  const onCountySelect = (next) => {
    handleCountyChange?.(next === 'all' ? 'all' : next);
  };

  if (isButton) {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`.trim()}>
        <Select value={selectRegion} onValueChange={onRegionSelect}>
          <SelectTrigger
            id={regionId}
            disabled={disabled || loadingRegions}
            className={BUTTON_TRIGGER_CLASS}
            aria-label="Region"
          >
            <SelectValue placeholder={loadingRegions ? 'Loading…' : 'All Regions'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {regions.map((region) => {
              const idVal = locationOptionId(region);
              return (
                <SelectItem key={idVal} value={String(idVal)}>
                  {locationOptionName(region)}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Select value={selectCounty} onValueChange={onCountySelect}>
          <SelectTrigger
            id={countyId}
            disabled={disabled || loadingCounties}
            className={BUTTON_TRIGGER_CLASS}
            aria-label="County"
          >
            <SelectValue placeholder={loadingCounties ? 'Loading…' : 'All Counties'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Counties</SelectItem>
            {counties.map((county) => {
              const idVal = locationOptionId(county);
              return (
                <SelectItem key={idVal} value={String(idVal)}>
                  {locationOptionName(county)}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${className}`.trim()}>
      <div className="min-w-[10rem] flex-1">
        {regionLabel ? (
          <label htmlFor={regionId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {regionLabel}
          </label>
        ) : null}
        <select
          id={regionId}
          value={selectRegion}
          disabled={disabled || loadingRegions}
          onChange={(e) => onRegionSelect(e.target.value)}
          className={DEFAULT_SELECT_CLASS}
        >
          <option value="all">{loadingRegions ? 'Loading regions...' : 'All Regions'}</option>
          {regions.map((region) => {
            const idVal = locationOptionId(region);
            return (
              <option key={idVal} value={String(idVal)}>
                {locationOptionName(region)}
              </option>
            );
          })}
        </select>
      </div>
      <div className="min-w-[10rem] flex-1">
        {countyLabel ? (
          <label htmlFor={countyId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {countyLabel}
          </label>
        ) : null}
        <select
          id={countyId}
          value={selectCounty}
          disabled={disabled || loadingCounties}
          onChange={(e) => onCountySelect(e.target.value)}
          className={DEFAULT_SELECT_CLASS}
        >
          <option value="all">{loadingCounties ? 'Loading counties...' : 'All Counties'}</option>
          {counties.map((county) => {
            const idVal = locationOptionId(county);
            return (
              <option key={idVal} value={String(idVal)}>
                {locationOptionName(county)}
              </option>
            );
          })}
        </select>
      </div>
    </div>
  );
}

/** Parse select value to optional Integer id for API (null = all). */
export function regionFilterToId(value) {
  if (value == null || value === '' || value === 'all') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Parse select value to optional Integer countyId for API (null = all). */
export function countyFilterToId(value) {
  if (value == null || value === '' || value === 'all') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}
